/**
 * Phase 8-G — Shared StatsView component.
 *
 * Renders the Phase 8-C stats response. Reused by:
 *   - Top-level Stats page (`/stats`, also `/agency/stats` deep-link)
 *   - Admin Portal OrganisationStats page (`/admin/organisations/:id/stats`)
 *   - Admin Portal OverviewStats page (`/admin/overview/stats`)
 *
 * Guardrails:
 *   - No chart libraries. All charts are inline-CSS bars + SVG primitives
 *     (mirrors the Billing.tsx approach).
 *   - Phase 13 — no MUI primitives either. Everything is plain divs + custom
 *     SCSS so the page picks up the legacy theme variables. See
 *     `src/styles/pages/stats/stats.scss`.
 *   - No new npm dependencies.
 */
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  StatsResponse,
  StatsRiskDistribution,
  StatsWindow,
} from '../../lib/api/stats/stats-api';

/** Brand link mode: agency users link to /actions/queue?shop_slug=…
 *  (the legacy actions queue absorbed the deprecated /agency/proposals
 *  page during the FE consolidation wave). Admin users link to the
 *  admin shop detail view (currently the org detail page, since
 *  per-shop detail is post-Phase 8). */
export type BrandLinkMode = 'agency' | 'admin';

export interface StatsViewProps {
  data: StatsResponse | null;
  window: StatsWindow;
  onWindowChange: (w: StatsWindow) => void;
  loading: boolean;
  title: string;
  error?: string | null;
  /** Defaults to 'agency'. */
  brandLinkMode?: BrandLinkMode;
  /** Optional slot rendered at the right of the header (e.g. back button). */
  headerAction?: React.ReactNode;
  /** Optional org id used by admin brand links. */
  orgId?: string;
  /** Optional retry handler; renders a "Retry" button next to the error. */
  onRetry?: () => void;
  /**
   * Account type for label terminology. Agency orgs use "brands", enterprise
   * orgs use "business units". Defaults to 'agency'.
   */
  accountType?: 'agency' | 'enterprise';
}

// ---------- helpers ----------

/**
 * Format a millisecond duration in a humane way.
 * - <60s → "Ns" (e.g. "42s")
 * - <60m → "Nm Ns" (e.g. "5m 12s")
 * - else  → "Nh Nm" (e.g. "2h 5m")
 */
export function formatMs(ms: number | null): string {
  if (ms === null || ms === undefined || Number.isNaN(ms)) return '—';
  if (ms < 0) return '—';
  const totalSeconds = Math.round(ms / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes < 60) return `${minutes}m ${seconds}s`;
  const hours = Math.floor(minutes / 60);
  const remMinutes = minutes % 60;
  return `${hours}h ${remMinutes}m`;
}

function formatPct(value: number): string {
  if (!Number.isFinite(value)) return '0%';
  return `${Math.round(value * 100)}%`;
}

function safeRatio(num: number, denom: number): number {
  if (!denom || denom <= 0) return 0;
  return num / denom;
}

// ---------- sub-components ----------

const KpiCard: React.FC<{
  label: string;
  value: number | string;
  sublabel?: string;
}> = ({ label, value, sublabel }) => (
  <div className='statsCard'>
    <div className='statsCard__label'>{label}</div>
    <div className='statsCard__value'>{value}</div>
    {sublabel && <div className='statsCard__sublabel'>{sublabel}</div>}
  </div>
);

const PercentileCard: React.FC<{
  title: string;
  percentiles: { p50: number | null; p90: number | null; p99: number | null };
  fallbackMessage?: string;
}> = ({ title, percentiles, fallbackMessage }) => {
  const allNull =
    percentiles.p50 === null &&
    percentiles.p90 === null &&
    percentiles.p99 === null;

  return (
    <div className='statsSection'>
      <div className='statsSection__title'>{title}</div>
      {allNull && fallbackMessage ? (
        <div className='statsSection__info'>{fallbackMessage}</div>
      ) : (
        <div className='statsPercentiles'>
          {(['p50', 'p90', 'p99'] as const).map((key) => {
            const v = percentiles[key];
            return (
              <div className='statsPercentiles__cell' key={key}>
                <div className='statsPercentiles__label'>
                  {key.toUpperCase()}
                </div>
                <div className='statsPercentiles__value'>
                  {v === null ? '—' : formatMs(v)}
                </div>
                {v === null && (
                  <div className='statsPercentiles__note'>
                    insufficient data
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const HorizontalBarRow: React.FC<{
  label: React.ReactNode;
  count: number;
  maxCount: number;
  color?: string;
  href?: string;
}> = ({ label, count, maxCount, color, href }) => {
  const pct = maxCount > 0 ? Math.max(2, (count / maxCount) * 100) : 0;
  const barStyle: React.CSSProperties = {
    width: `${pct}%`,
    ...(color ? { backgroundColor: color } : {}),
  };
  const inner = (
    <>
      <div className='statsBarRow__head'>
        <div className='statsBarRow__label'>{label}</div>
        <div className='statsBarRow__count'>{count}</div>
      </div>
      <div className='statsBarRow__track'>
        <div className='statsBarRow__fill' style={barStyle} />
      </div>
    </>
  );

  if (href) {
    return (
      <Link to={href} className='statsBarRow statsBarRow--link'>
        {inner}
      </Link>
    );
  }
  return <div className='statsBarRow'>{inner}</div>;
};

const PlatformBarChart: React.FC<{
  data: Array<{ platform: string; count: number }>;
}> = ({ data }) => {
  const maxCount = useMemo(
    () => data.reduce((m, b) => (b.count > m ? b.count : m), 0),
    [data]
  );

  if (data.length === 0) {
    return (
      <div className='statsSection__muted'>
        No platform activity in this window.
      </div>
    );
  }

  return (
    <div className='statsPlatformChart'>
      <div className='statsPlatformChart__bars'>
        {data.map((b) => {
          const pct =
            maxCount > 0 ? Math.max(2, (b.count / maxCount) * 100) : 0;
          return (
            <div
              key={b.platform}
              className='statsPlatformChart__col'
              title={`${b.platform} · ${b.count}`}
            >
              <div className='statsPlatformChart__count'>{b.count}</div>
              <div
                className='statsPlatformChart__bar'
                style={{ height: `${pct}%` }}
              />
            </div>
          );
        })}
      </div>
      <div className='statsPlatformChart__labels'>
        {data.map((b) => (
          <div key={`label-${b.platform}`} className='statsPlatformChart__name'>
            {b.platform}
          </div>
        ))}
      </div>
    </div>
  );
};

const ExecutorSuccessRow: React.FC<{
  platform: string;
  success: number;
  failure: number;
}> = ({ platform, success, failure }) => {
  const total = success + failure;
  const successPct = total > 0 ? (success / total) * 100 : 0;
  const failurePct = total > 0 ? (failure / total) * 100 : 0;
  return (
    <div className='statsExecutorRow'>
      <div className='statsExecutorRow__head'>
        <div className='statsExecutorRow__platform'>{platform}</div>
        <div className='statsExecutorRow__counts'>
          {success} ok · {failure} fail
        </div>
      </div>
      <div className='statsExecutorRow__track'>
        <div
          className='statsExecutorRow__fill statsExecutorRow__fill--success'
          style={{ width: `${successPct}%` }}
        />
        <div
          className='statsExecutorRow__fill statsExecutorRow__fill--failure'
          style={{ width: `${failurePct}%` }}
        />
      </div>
    </div>
  );
};

// ---------- Risk distribution donut (inline SVG) ----------

const RISK_COLORS: Record<string, string> = {
  low: '#22c55e', // green
  medium: '#f59e0b', // orange
  high: '#ef4444', // red
  critical: '#7f1d1d', // dark-red
};

const RISK_LABELS: Record<string, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  critical: 'Critical',
};

/**
 * Build an SVG path for a single donut arc.
 * `startFrac` / `endFrac` are 0..1 around the circle, starting at 12 o'clock,
 * sweeping clockwise. `r` = outer radius, `inner` = inner cut-out radius.
 */
function donutArcPath(
  startFrac: number,
  endFrac: number,
  cx: number,
  cy: number,
  r: number,
  inner: number
): string {
  // Guard tiny slivers and full-circle edge cases.
  const sweep = endFrac - startFrac;
  if (sweep <= 0) return '';
  const fullCircle = sweep >= 0.9999;
  const angle = (frac: number) => frac * Math.PI * 2 - Math.PI / 2;
  const a0 = angle(startFrac);
  const a1 = angle(endFrac);

  const x0 = cx + r * Math.cos(a0);
  const y0 = cy + r * Math.sin(a0);
  const x1 = cx + r * Math.cos(a1);
  const y1 = cy + r * Math.sin(a1);
  const xi0 = cx + inner * Math.cos(a0);
  const yi0 = cy + inner * Math.sin(a0);
  const xi1 = cx + inner * Math.cos(a1);
  const yi1 = cy + inner * Math.sin(a1);
  const largeArc = sweep > 0.5 ? 1 : 0;

  if (fullCircle) {
    // Two half-circles to avoid SVG's "M==L" full-arc ambiguity.
    const mx = cx + r;
    const my = cy;
    const mxi = cx + inner;
    const myi = cy;
    return [
      `M ${mx} ${my}`,
      `A ${r} ${r} 0 1 1 ${cx - r} ${cy}`,
      `A ${r} ${r} 0 1 1 ${mx} ${my}`,
      `M ${mxi} ${myi}`,
      `A ${inner} ${inner} 0 1 0 ${cx - inner} ${cy}`,
      `A ${inner} ${inner} 0 1 0 ${mxi} ${myi}`,
      'Z',
    ].join(' ');
  }

  return [
    `M ${x0} ${y0}`,
    `A ${r} ${r} 0 ${largeArc} 1 ${x1} ${y1}`,
    `L ${xi1} ${yi1}`,
    `A ${inner} ${inner} 0 ${largeArc} 0 ${xi0} ${yi0}`,
    'Z',
  ].join(' ');
}

const RiskDonut: React.FC<{ distribution: StatsRiskDistribution }> = ({
  distribution,
}) => {
  const order = ['low', 'medium', 'high', 'critical'] as const;
  const buckets = order.map((k) => ({
    key: k,
    count: Math.max(0, Number(distribution[k] ?? 0)),
  }));
  const total = buckets.reduce((sum, b) => sum + b.count, 0);

  const size = 160;
  const cx = size / 2;
  const cy = size / 2;
  const r = 70;
  const inner = 44;

  if (total === 0) {
    return (
      <div className='statsRiskDonut statsRiskDonut--empty'>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          <circle cx={cx} cy={cy} r={r} fill='#f0f1f3' />
          <circle cx={cx} cy={cy} r={inner} fill='#ffffff' />
        </svg>
        <div className='statsSection__muted'>
          No risk data in this window.
        </div>
      </div>
    );
  }

  let cursor = 0;
  const arcs = buckets.map((b) => {
    const frac = b.count / total;
    const start = cursor;
    const end = cursor + frac;
    cursor = end;
    return { ...b, start, end, frac };
  });

  return (
    <div className='statsRiskDonut'>
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        role='img'
        aria-label='Risk level distribution'
      >
        {arcs.map((a) =>
          a.frac > 0 ? (
            <path
              key={a.key}
              d={donutArcPath(a.start, a.end, cx, cy, r, inner)}
              fill={RISK_COLORS[a.key]}
              opacity={0.9}
            >
              <title>{`${RISK_LABELS[a.key]}: ${a.count} (${Math.round(
                a.frac * 100
              )}%)`}</title>
            </path>
          ) : null
        )}
        <text
          x={cx}
          y={cy - 2}
          textAnchor='middle'
          fontSize='22'
          fontWeight={600}
          fill='#1f2937'
        >
          {total}
        </text>
        <text
          x={cx}
          y={cy + 16}
          textAnchor='middle'
          fontSize='11'
          fill='#6b7280'
        >
          proposals
        </text>
      </svg>
      <div className='statsRiskDonut__legend'>
        {arcs.map((a) => (
          <div key={a.key} className='statsRiskDonut__legendRow'>
            <span
              className='statsRiskDonut__swatch'
              style={{ backgroundColor: RISK_COLORS[a.key] }}
            />
            <span className='statsRiskDonut__legendName'>
              {RISK_LABELS[a.key]}
            </span>
            <span className='statsRiskDonut__legendValue'>
              {a.count} ({Math.round(a.frac * 100)}%)
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ---------- main component ----------

const StatsView: React.FC<StatsViewProps> = ({
  data,
  window,
  onWindowChange,
  loading,
  title,
  error,
  brandLinkMode = 'agency',
  headerAction,
  orgId,
  onRetry,
  accountType = 'agency',
}) => {
  const summary = data?.summary;
  const shopPluralLower =
    accountType === 'enterprise' ? 'business units' : 'brands';
  const approvedPct = summary
    ? formatPct(safeRatio(summary.proposals_approved, summary.proposals_total))
    : '0%';

  const maxShopCount = useMemo(() => {
    if (!data?.approved_per_shop?.length) return 0;
    return data.approved_per_shop.reduce(
      (m, s) => (s.count > m ? s.count : m),
      0
    );
  }, [data?.approved_per_shop]);

  const maxToolCount = useMemo(() => {
    if (!data?.proposals_by_tool?.length) return 0;
    return data.proposals_by_tool.reduce(
      (m, t) => (t.count > m ? t.count : m),
      0
    );
  }, [data?.proposals_by_tool]);

  const buildShopHref = (slug: string): string | undefined => {
    if (brandLinkMode === 'agency') {
      return `/actions/queue?shop_slug=${encodeURIComponent(slug)}`;
    }
    // admin: link back to org detail (per-shop admin page lands later)
    if (orgId) return `/admin/organisations/${encodeURIComponent(orgId)}`;
    return undefined;
  };

  const windowOptions: Array<{ value: StatsWindow; label: string }> = [
    { value: '7d', label: 'Last 7 days' },
    { value: '30d', label: 'Last 30 days' },
    { value: '90d', label: 'Last 90 days' },
  ];

  const autoApprovalRate = summary?.auto_approval_rate ?? 0;
  const autoApprovalBarWidth = Math.max(
    2,
    Math.min(100, autoApprovalRate * 100)
  );

  return (
    <div className='statsView'>
      {/* ---------- Header (only render when a title or headerAction was passed) ---------- */}
      {(title || headerAction) && (
        <div className='statsView__header'>
          {title && <h2 className='statsView__title'>{title}</h2>}
          {headerAction && (
            <div className='statsView__headerAction'>{headerAction}</div>
          )}
        </div>
      )}

      {/* ---------- Window chips ---------- */}
      <div className='statsView__chips'>
        {windowOptions.map((opt) => (
          <button
            key={opt.value}
            type='button'
            className={`statsView__chip ${
              window === opt.value ? 'statsView__chip--active' : ''
            }`}
            onClick={() => onWindowChange(opt.value)}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {error && (
        <div className='statsView__error' role='alert'>
          <span>{error}</span>
          {onRetry && (
            <button
              type='button'
              className='statsView__retryBtn'
              onClick={onRetry}
            >
              Retry
            </button>
          )}
        </div>
      )}

      {loading || !data ? (
        <div className='statsView__loading'>Loading stats…</div>
      ) : (
        <>
          {/* ---------- KPI cards row ---------- */}
          <div className='statsCardGrid'>
            <KpiCard
              label='Total proposals'
              value={summary?.proposals_total ?? 0}
            />
            <KpiCard
              label='Approved'
              value={summary?.proposals_approved ?? 0}
              sublabel={`${approvedPct} of total`}
            />
            <KpiCard
              label='Denied'
              value={summary?.proposals_denied ?? 0}
            />
            <KpiCard
              label='Pending'
              value={summary?.proposals_pending ?? 0}
            />
          </div>

          {/* ---------- Auto-approval rate ---------- */}
          <div className='statsSection'>
            <div className='statsSection__eyebrow'>Auto-approval rate</div>
            <div className='statsAutoApproval__row'>
              <div className='statsAutoApproval__value'>
                {formatPct(autoApprovalRate)}
              </div>
              <div className='statsAutoApproval__caption'>
                auto-approved by rules
              </div>
              <div className='statsAutoApproval__badge'>
                {summary?.proposals_auto_approved ?? 0} proposals
              </div>
            </div>
            {/* Sparkline: single bar vs 100% reference */}
            <div className='statsAutoApproval__track'>
              <div
                className='statsAutoApproval__fill'
                style={{ width: `${autoApprovalBarWidth}%` }}
              />
            </div>
            <div className='statsAutoApproval__meta'>
              <span>
                <strong>{summary?.active_employees ?? 0}</strong> active
                employees
              </span>
              <span>
                <strong>{summary?.active_shops ?? 0}</strong> active shops
              </span>
            </div>
          </div>

          {/* ---------- Time to approve ---------- */}
          <PercentileCard
            title='Time to approve'
            percentiles={data.time_to_approve_ms}
          />

          {/* ---------- Risk distribution (inline SVG donut) ---------- */}
          <div className='statsSection'>
            <div className='statsSection__title'>Risk distribution</div>
            <div className='statsSection__divider' />
            <RiskDonut distribution={data.risk_distribution ?? {}} />
          </div>

          {/* ---------- Approved per shop leaderboard ---------- */}
          <div className='statsSection'>
            <div className='statsSection__title'>{`Approved by ${shopPluralLower} (top 10)`}</div>
            <div className='statsSection__divider' />
            {data.approved_per_shop.length === 0 ? (
              <div className='statsSection__muted'>
                No approved proposals in this window.
              </div>
            ) : (
              <div className='statsBarRows'>
                {data.approved_per_shop.map((row) => (
                  <HorizontalBarRow
                    key={row.shop_slug}
                    label={row.shop_name || row.shop_slug}
                    count={row.count}
                    maxCount={maxShopCount}
                    href={buildShopHref(row.shop_slug)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* ---------- Proposals by platform ---------- */}
          <div className='statsSection'>
            <div className='statsSection__title'>Proposals by platform</div>
            <div className='statsSection__divider' />
            <PlatformBarChart data={data.proposals_by_platform} />
          </div>

          {/* ---------- Proposals by tool (top 10) ---------- */}
          <div className='statsSection'>
            <div className='statsSection__title'>Top tools used (top 10)</div>
            <div className='statsSection__divider' />
            {data.proposals_by_tool.length === 0 ? (
              <div className='statsSection__muted'>
                No tool usage in this window.
              </div>
            ) : (
              <div className='statsToolTable'>
                <div className='statsToolTable__head'>
                  <div className='statsToolTable__col statsToolTable__col--name'>
                    Tool
                  </div>
                  <div className='statsToolTable__col statsToolTable__col--count'>
                    Count
                  </div>
                  <div className='statsToolTable__col statsToolTable__col--share'>
                    Share
                  </div>
                </div>
                {data.proposals_by_tool.map((row) => {
                  const pct =
                    maxToolCount > 0
                      ? Math.max(2, (row.count / maxToolCount) * 100)
                      : 0;
                  return (
                    <div className='statsToolTable__row' key={row.tool_name}>
                      <div className='statsToolTable__col statsToolTable__col--name'>
                        {row.tool_name}
                      </div>
                      <div className='statsToolTable__col statsToolTable__col--count'>
                        {row.count}
                      </div>
                      <div className='statsToolTable__col statsToolTable__col--share'>
                        <div className='statsToolTable__bar'>
                          <div
                            className='statsToolTable__barFill'
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* ---------- Executor success rate ---------- */}
          <div className='statsSection'>
            <div className='statsSection__title'>Executor success rate</div>
            <div className='statsSection__divider' />
            {data.executor_success_rate.length === 0 ? (
              <div className='statsSection__muted'>
                No executor activity in this window.
              </div>
            ) : (
              <div className='statsExecutorRows'>
                {data.executor_success_rate.map((row) => (
                  <ExecutorSuccessRow
                    key={row.platform}
                    platform={row.platform}
                    success={row.success}
                    failure={row.failure}
                  />
                ))}
              </div>
            )}
          </div>

          {/* ---------- Slack responsiveness ---------- */}
          <PercentileCard
            title='Slack responsiveness'
            percentiles={data.slack_responsiveness_ms}
            fallbackMessage='Audit event not yet emitted. Slack responsiveness will populate once the backend emits `slack_notification_sent` events.'
          />

          {/* Window footer */}
          <div className='statsView__footer'>
            Window: {data.window_start} → {data.window_end}
          </div>
        </>
      )}
    </div>
  );
};

export default StatsView;
